import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'how-to-request-your-adoption-records';

export const RequestAdoptionRecords = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        If you were adopted, your history is usually split across two different kinds of record,
        held by different people, under slightly different rules. Understanding the difference
        first saves you writing to the wrong place.
      </p>
      <GuideChecklist
        items={[
          <>
            <strong className="text-ink">Your original birth record</strong> — your birth
            certificate as it was before the adoption, and often the only document that names
            your birth parents directly. This is held by the General Register Office (GRO), not
            the adoption agency.
          </>,
          <>
            <strong className="text-ink">Your adoption case file</strong> — the fuller story:
            why adoption was decided on, the matching process, life story work, panel records
            and, in many cases, arrangements for contact with your birth family. This is held by
            the adoption agency: your local authority&apos;s adoption team, a regional adoption
            agency, or a voluntary adoption agency.
          </>,
        ]}
      />
      <p>Both are worth requesting if you want the full picture.</p>
    </GuideSection>

    <GuideSection eyebrow="Your birth record" title="Getting your original birth certificate">
      <p>
        If you know your birth details (your birth name, birth parents&apos; names, or the district
        you were born in), you can order your original birth certificate directly from the
        General Register Office. If you don&apos;t know your birth details, you&apos;ll need to apply
        through the GRO&apos;s Birth Certificate Information Before Adoption (BIBA) service instead.
      </p>
      <GuideNote>
        <p>
          <strong>If you were adopted before 12 November 1975</strong> and don&apos;t already know
          your birth details, the law requires you to attend a counselling session with an
          approved adoption adviser before you can be given them — this exists to make sure
          support is available for what can be difficult information, not to gatekeep it from
          you.
        </p>
      </GuideNote>
      <p>
        Full details of how to apply are on{' '}
        <SourceLink href="https://www.gov.uk/adoption-records">gov.uk&apos;s adoption records page</SourceLink>
        .
      </p>
    </GuideSection>

    <GuideSection eyebrow="Your case file" title="Requesting your file from the adoption agency">
      <p>
        This part is a Subject Access Request in the usual sense — see our{' '}
        <Link to="/guides/what-is-a-subject-access-request" className="text-brand underline hover:opacity-90">
          plain-English guide
        </Link>{' '}
        if you want the general mechanics. What to ask an adoption agency for specifically:
      </p>
      <GuideChecklist
        items={[
          'The panel report and matching records that led to your placement for adoption.',
          'Life story work — the account, often with photos, put together to help you understand your own history.',
          'Any letterbox or indirect contact arrangements, and copies of letters exchanged if the agency held or facilitated them.',
          "The supervising social worker's visit records during the adoption placement, before the order was finalised.",
          'Records of any contact or attempted contact from your birth family since the adoption.',
        ]}
      />
      <p>
        If your adoption agency has since merged into a regional adoption agency, or closed,
        your local authority&apos;s adoption team can usually tell you who now holds the records —
        adoption files have long statutory retention and don&apos;t simply disappear when an agency
        closes.
      </p>
    </GuideSection>

    <GuideCTA />

    <GuideSection eyebrow="Support" title="You don't have to read this alone">
      <p>
        Adoption records can include difficult material — the circumstances that led to adoption
        being decided on, or information about birth family you&apos;ve never had before. Adoption
        agencies are generally expected to offer counselling or support around this, and
        independent adoption support services exist specifically for this. It is reasonable to
        ask the agency what support is available before, or while, records are released to you.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="adoption-records-faq">
        <GuideFaqItem
          q="Will this tell me who my birth parents are?"
          a="Your original birth certificate usually names your birth mother, and your father if he was registered on it. Your adoption case file may include a great deal more context about your birth family. Neither is guaranteed to include current contact details, since that's separate personal data about someone else."
        />
        <GuideFaqItem
          q="Can I contact my birth family through this?"
          a="Not directly through a SAR. The Adoption Contact Register is the formal route for adopted people and birth relatives to indicate they'd welcome contact — your adoption agency or an intermediary agency can also help you approach this carefully and with support."
        />
        <GuideFaqItem
          q="I was adopted from care. Do I also need to ask the council separately?"
          a="Yes, if you want the fuller picture. The council usually holds records from before the adoption order — the original care proceedings and any placements that came before — which is separate from the adoption agency's own file. See our guide to requesting care records from the council."
        />
        <GuideFaqItem
          q="Is there an age limit?"
          a="You need to be 18 or over to access your original birth records. Your right to request your adoption case file under a SAR isn't subject to the same age limit, though the adoption agency's own practice around counselling and support may still apply depending on your circumstances."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        Adoption records sit under the{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2002/38/contents">
          Adoption and Children Act 2002
        </SourceLink>{' '}
        as well as ordinary data protection law. Start your birth-record request at{' '}
        <SourceLink href="https://www.gov.uk/adoption-records">gov.uk</SourceLink>, and read the
        ICO&apos;s{' '}
        <SourceLink href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/individual-rights/right-of-access/">
          right of access guidance
        </SourceLink>{' '}
        for how the SAR side works.
      </p>
    </GuideSection>

    <GuideCTA
      heading="We can request your adoption case file for you"
      body="Add the adoption agency or local authority you remember, and we'll draft the request, send it, and keep track of the deadline while you take things at your own pace."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default RequestAdoptionRecords;
